export interface RateLimiterOptions {
  maxCalls: number;
  windowMs: number;
}

export class RateLimiter {
  private readonly maxCalls: number;
  private readonly windowMs: number;
  private timestamps: number[] = [];

  constructor({ maxCalls, windowMs }: RateLimiterOptions) {
    this.maxCalls = maxCalls;
    this.windowMs = windowMs;
  }

  private prune(now: number): void {
    const cutoff = now - this.windowMs;
    this.timestamps = this.timestamps.filter((timestamp) => timestamp > cutoff);
  }

  tryAcquire(now: number = Date.now()): boolean {
    this.prune(now);

    if (this.timestamps.length >= this.maxCalls) {
      return false;
    }

    this.timestamps.push(now);
    return true;
  }

  reset(): void {
    this.timestamps = [];
  }

  isIdle(now: number = Date.now()): boolean {
    this.prune(now);
    return this.timestamps.length === 0;
  }
}

export class KeyedRateLimiter {
  private readonly options: RateLimiterOptions;
  private readonly limiters = new Map<string, RateLimiter>();
  private nextPruneAt = 0;

  constructor(options: RateLimiterOptions) {
    this.options = options;
  }

  tryAcquire(key: string, now: number = Date.now()): boolean {
    if (now >= this.nextPruneAt) {
      this.pruneInactive(now);
    }

    let limiter = this.limiters.get(key);
    if (!limiter) {
      limiter = new RateLimiter(this.options);
      this.limiters.set(key, limiter);
    }
    return limiter.tryAcquire(now);
  }

  reset(): void {
    this.limiters.clear();
    this.nextPruneAt = 0;
  }

  pruneInactive(now: number = Date.now()): number {
    let removedCount = 0;

    this.limiters.forEach((limiter, key) => {
      if (limiter.isIdle(now)) {
        this.limiters.delete(key);
        removedCount += 1;
      }
    });

    this.nextPruneAt = now + this.options.windowMs;
    return removedCount;
  }
}
